import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../i18n/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const CATEGORIES = [
  { value: 'launcher',      tKey: 'appCatLauncher',      hint: 'Steam, Epic Games, Battle.net...' },
  { value: 'competitive',   tKey: 'appCatCompetitive',   hint: 'FACEIT, ESEA...' },
  { value: 'communication', tKey: 'appCatCommunication', hint: 'Discord, TeamSpeak...' },
  { value: 'system',        tKey: 'appCatSystem',        hint: 'NVIDIA App, AMD Adrenalin...' },
  { value: 'peripheral',    tKey: 'appCatPeripheral',    hint: 'Logitech G HUB, Razer Synapse...' },
  { value: 'streaming',     tKey: 'appCatStreaming',      hint: 'OBS Studio...' },
  { value: 'browser',       tKey: 'appCatBrowser',       hint: 'Chrome, Yandex Browser...' },
];

const PRESETS = [
  { name: 'Steam',           category: 'launcher',      icon_url: 'https://store.steampowered.com/favicon.ico',    exe_path: 'C:\\Program Files (x86)\\Steam\\steam.exe' },
  { name: 'Epic Games',      category: 'launcher',      icon_url: 'https://store.epicgames.com/favicon.ico',       exe_path: 'C:\\Program Files (x86)\\Epic Games\\Launcher\\Portal\\Binaries\\Win32\\EpicGamesLauncher.exe' },
  { name: 'Battle.net',      category: 'launcher',      icon_url: 'https://www.blizzard.com/favicon.ico',          exe_path: 'C:\\Program Files (x86)\\Battle.net\\Battle.net.exe' },
  { name: 'Riot Client',     category: 'launcher',      icon_url: 'https://www.riotgames.com/favicon.ico',         exe_path: 'C:\\Riot Games\\Riot Client\\RiotClientServices.exe' },
  { name: 'EA App',          category: 'launcher',      icon_url: 'https://www.ea.com/favicon.ico',                exe_path: 'C:\\Program Files\\Electronic Arts\\EA Desktop\\EA Desktop\\EADesktop.exe' },
  { name: 'Ubisoft Connect', category: 'launcher',      icon_url: 'https://ubisoftconnect.com/favicon.ico',        exe_path: 'C:\\Program Files (x86)\\Ubisoft\\Ubisoft Game Launcher\\UbisoftConnect.exe' },
  { name: 'GOG Galaxy',      category: 'launcher',      icon_url: 'https://www.gog.com/favicon.ico',               exe_path: 'C:\\Program Files (x86)\\GOG Galaxy\\GalaxyClient.exe' },
  { name: 'FACEIT',          category: 'competitive',   icon_url: 'https://www.faceit.com/favicon.ico',            exe_path: 'C:\\Users\\%USERNAME%\\AppData\\Local\\FACEIT\\FACEIT.exe' },
  { name: 'Discord',         category: 'communication', icon_url: 'https://discord.com/favicon.ico',               exe_path: 'C:\\Users\\%USERNAME%\\AppData\\Local\\Discord\\Update.exe', args: '--processStart Discord.exe' },
  { name: 'TeamSpeak 3',     category: 'communication', icon_url: 'https://www.teamspeak.com/favicon.ico',         exe_path: 'C:\\Program Files\\TeamSpeak 3 Client\\ts3client_win64.exe' },
  { name: 'NVIDIA App',      category: 'system',        icon_url: 'https://www.nvidia.com/favicon.ico',            exe_path: 'C:\\Program Files\\NVIDIA Corporation\\NVIDIA app\\CEF\\nvidia_app.exe' },
  { name: 'Logitech G HUB',  category: 'peripheral',    icon_url: 'https://www.logitechg.com/favicon.ico',         exe_path: 'C:\\Program Files\\LGHUB\\lghub.exe' },
  { name: 'Razer Synapse',   category: 'peripheral',    icon_url: 'https://www.razer.com/favicon.ico',             exe_path: 'C:\\Program Files (x86)\\Razer\\Synapse3\\WPFUI\\Framework\\Razer Synapse 3 Host\\Razer Synapse 3 Host.exe' },
  { name: 'OBS Studio',      category: 'streaming',     icon_url: 'https://obsproject.com/favicon.ico',            exe_path: 'C:\\Program Files\\obs-studio\\bin\\64bit\\obs64.exe' },
  { name: 'Google Chrome',   category: 'browser',       icon_url: 'https://www.google.com/favicon.ico',            exe_path: 'C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe' },
  { name: 'Yandex Browser',  category: 'browser',       icon_url: 'https://browser.yandex.com/favicon.ico',       exe_path: 'C:\\Users\\%USERNAME%\\AppData\\Local\\Yandex\\YandexBrowser\\Application\\browser.exe' },
];

const EMPTY = { name: '', category: 'launcher', icon_url: '', exe_path: '', args: '', sort_order: 0, enabled: true };

export default function Apps() {
  const { t } = useLang();
  const [apps, setApps]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [showPresets, setShowPresets] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const catLabel = (c) => t(c.tKey);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/apps/owner`, { headers });
      if (r.ok) setApps(await r.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    const d = modal.data;
    if (!d.name.trim() || !d.exe_path.trim()) return alert(t('appSaveRequired'));
    const url  = modal.mode === 'edit' ? `${API}/apps/owner/${d.id}` : `${API}/apps/owner`;
    const meth = modal.mode === 'edit' ? 'PUT' : 'POST';
    const r = await fetch(url, { method: meth, headers, body: JSON.stringify(d) });
    if (r.ok) { setModal(null); load(); }
    else alert(t('error') + ': ' + (await r.text()));
  }

  async function del(id) {
    if (!window.confirm(t('confirmDelete'))) return;
    await fetch(`${API}/apps/owner/${id}`, { method: 'DELETE', headers });
    load();
  }

  async function toggle(app) {
    await fetch(`${API}/apps/owner/${app.id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ ...app, enabled: !app.enabled }),
    });
    load();
  }

  function addPreset(preset) {
    setModal({ mode: 'add', data: { ...EMPTY, ...preset } });
    setShowPresets(false);
  }

  const filtered = filterCat === 'all' ? apps : apps.filter(a => a.category === filterCat);
  const catCounts = {};
  apps.forEach(a => { catCounts[a.category] = (catCounts[a.category] || 0) + 1; });

  return (
    <div style={{ padding: '28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{t('appsTitle')}</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>{t('appsSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowPresets(true)} style={btnStyle('#7c3aed')}>
            {t('appsPresetsBtn')}
          </button>
          <button onClick={() => setModal({ mode: 'add', data: { ...EMPTY } })} style={btnStyle('#0ea5e9')}>
            {t('appsAddBtn')}
          </button>
        </div>
      </div>

      {/* FILTER */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[{ value: 'all', label: `${t('appsAllFilter')} (${apps.length})` },
          ...CATEGORIES.map(c => ({ value: c.value, label: `${catLabel(c)} (${catCounts[c.value] || 0})` }))
        ].map(f => (
          <button key={f.value} onClick={() => setFilterCat(f.value)}
            style={{ padding: '7px 16px', borderRadius: 20, border: '1px solid',
              borderColor: filterCat === f.value ? '#0ea5e9' : '#374151',
              background: filterCat === f.value ? 'rgba(14,165,233,0.1)' : 'transparent',
              color: filterCat === f.value ? '#0ea5e9' : '#9ca3af',
              cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* TABLE */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', background: '#111827', borderRadius: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{t('appsNone')}</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>{t('appsNoneHint')}</div>
        </div>
      ) : (
        <div style={{ background: '#111827', borderRadius: 16, overflow: 'hidden', border: '1px solid #1f2937' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0d1117', borderBottom: '1px solid #1f2937' }}>
                {['', t('appColName'), t('appColCategory'), t('appColExePath'), t('appColOrder'), t('appColStatus'), t('colActions')].map((h, i) => (
                  <th key={i} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12,
                    fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((app, i) => {
                const cat = CATEGORIES.find(c => c.value === app.category);
                return (
                  <tr key={app.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid #1f2937' : 'none',
                    transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#0d1117'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 16px' }}>
                      {app.icon_url
                        ? <img src={app.icon_url} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        : <span style={{ fontSize: 28 }}>{cat ? t(cat.tKey).split(' ')[0] : '🖥️'}</span>
                      }
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{app.name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: '#1f2937', padding: '3px 10px', borderRadius: 20, fontSize: 12, color: '#9ca3af' }}>
                        {cat ? catLabel(cat) : app.category}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280', maxWidth: 260,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={app.exe_path}>
                      {app.exe_path}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 13 }}>{app.sort_order}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <div onClick={() => toggle(app)} style={{
                          width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer',
                          background: app.enabled ? '#22c55e' : '#374151', transition: 'background 0.2s',
                        }}>
                          <div style={{ position: 'absolute', top: 3, left: app.enabled ? 21 : 3,
                            width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                        <span style={{ fontSize: 12, color: app.enabled ? '#22c55e' : '#6b7280' }}>
                          {app.enabled ? t('appActive') : t('appHidden')}
                        </span>
                      </label>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setModal({ mode: 'edit', data: { ...app } })}
                          style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #374151',
                            background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}>
                          ✏️ {t('edit')}
                        </button>
                        <button onClick={() => del(app.id)}
                          style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #374151',
                            background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {modal && (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={modalStyle}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20 }}>
              {modal.mode === 'add' ? t('appAddTitle') : t('appEditTitle')}
            </h2>

            <div style={fieldGroup}>
              <label style={labelStyle}>{t('appNameLabel')}</label>
              <input style={inputStyle} value={modal.data.name} placeholder="Steam"
                onChange={e => setModal(m => ({ ...m, data: { ...m.data, name: e.target.value } }))} />
            </div>

            <div style={fieldGroup}>
              <label style={labelStyle}>{t('appCategoryLabel')}</label>
              <select style={inputStyle} value={modal.data.category}
                onChange={e => setModal(m => ({ ...m, data: { ...m.data, category: e.target.value } }))}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{catLabel(c)} — {c.hint}</option>)}
              </select>
            </div>

            <div style={fieldGroup}>
              <label style={labelStyle}>{t('appIconLabel')}</label>
              <input style={inputStyle} value={modal.data.icon_url || ''}
                placeholder="https://example.com/favicon.ico"
                onChange={e => setModal(m => ({ ...m, data: { ...m.data, icon_url: e.target.value } }))} />
              {modal.data.icon_url && (
                <img src={modal.data.icon_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, marginTop: 8, objectFit: 'contain' }}
                  onError={e => e.target.style.display = 'none'} />
              )}
            </div>

            <div style={fieldGroup}>
              <label style={labelStyle}>{t('appExeLabel')}</label>
              <input style={inputStyle} value={modal.data.exe_path}
                placeholder="C:\Program Files\Steam\steam.exe"
                onChange={e => setModal(m => ({ ...m, data: { ...m.data, exe_path: e.target.value } }))} />
            </div>

            <div style={fieldGroup}>
              <label style={labelStyle}>{t('appArgsLabel')}</label>
              <input style={inputStyle} value={modal.data.args || ''}
                placeholder="--processStart Discord.exe"
                onChange={e => setModal(m => ({ ...m, data: { ...m.data, args: e.target.value } }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>{t('appSortLabel')}</label>
                <input type="number" style={inputStyle} value={modal.data.sort_order}
                  onChange={e => setModal(m => ({ ...m, data: { ...m.data, sort_order: +e.target.value } }))} />
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>{t('appColStatus')}</label>
                <select style={inputStyle} value={modal.data.enabled ? 'true' : 'false'}
                  onChange={e => setModal(m => ({ ...m, data: { ...m.data, enabled: e.target.value === 'true' } }))}>
                  <option value="true">{t('appActiveOption')}</option>
                  <option value="false">{t('appHidden')}</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)}
                style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #374151',
                  background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}>
                {t('cancelShort')}
              </button>
              <button onClick={save} style={btnStyle('#0ea5e9')}>
                {modal.mode === 'add' ? t('appAddBtn2') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRESETS MODAL */}
      {showPresets && (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && setShowPresets(false)}>
          <div style={{ ...modalStyle, maxWidth: 700 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>{t('presetsModalTitle')}</h2>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>{t('presetsModalHint')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
              {PRESETS.map(p => {
                const cat = CATEGORIES.find(c => c.value === p.category);
                return (
                  <div key={p.name} onClick={() => addPreset(p)}
                    style={{ background: '#0d1117', border: '1px solid #1f2937', borderRadius: 12,
                      padding: 14, cursor: 'pointer', transition: 'border-color 0.2s', display: 'flex',
                      alignItems: 'center', gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#7c3aed'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#1f2937'}>
                    <img src={p.icon_url} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }}
                      onError={e => e.target.style.display = 'none'} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {cat ? catLabel(cat) : p.category}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button onClick={() => setShowPresets(false)}
                style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #374151',
                  background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}>
                {t('closeBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle = (color) => ({
  padding: '10px 20px', borderRadius: 10, border: 'none',
  background: color, color: '#fff', cursor: 'pointer',
  fontSize: 14, fontWeight: 600,
});

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle = {
  background: '#111827', border: '1px solid #1f2937', borderRadius: 20,
  padding: 32, width: '90%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
};

const fieldGroup = { marginBottom: 16 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#9ca3af', marginBottom: 6 };
const inputStyle = {
  width: '100%', background: '#0d1117', border: '1px solid #374151',
  borderRadius: 10, padding: '10px 14px', color: '#f9fafb', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
